import React from 'react';
import { Alert } from 'react-bootstrap';
import { FaExclamationTriangle, FaBan, FaInfoCircle } from 'react-icons/fa';
import { ItemStockInfo } from '../../hooks/useStockStatus';
import './StockAlert.scss';

interface StockAlertProps {
  hasShortage: boolean;
  hasWarning: boolean;
  shortageInfo?: ItemStockInfo;
  warningInfo?: ItemStockInfo;
}

/**
 * StockAlert Component
 * 
 * Displays a prominent alert banner explaining stock issues for an item.
 * Used in the SelectorModal to inform users about stock availability.
 */
const StockAlert: React.FC<StockAlertProps> = ({
  hasShortage,
  hasWarning,
  shortageInfo,
  warningInfo,
}) => {
  if (!hasShortage && !hasWarning) {
    return null;
  }

  const isShortage = hasShortage;
  const info = isShortage ? shortageInfo : warningInfo;

  if (!info) return null;

  // Build detailed message
  const buildMessage = (): { title: string; details: string } => {
    if (isShortage) {
      if (info.missing_ingredients === null) {
        // Twin ingredient system - no specific ingredients listed
        return {
          title: 'Artículo sin inventario',
          details: `${info.item_name} no tiene stock disponible actualmente.`,
        };
      } else if (info.missing_ingredients && info.missing_ingredients.length > 0) {
        // Missing specific ingredients
        return {
          title: 'Artículo sin inventario por ingredientes faltantes',
          details: `${info.item_name} no puede prepararse porque faltan los siguientes ingredientes: ${info.missing_ingredients.join(', ')}.`,
        };
      } else {
        // General shortage
        return {
          title: 'Artículo sin inventario',
          details: `${info.item_name} no tiene stock disponible.`,
        };
      }
    } else {
      // Warning - low stock
      if (info.low_stock_ingredients && info.low_stock_ingredients.length > 0) {
        return {
          title: 'Inventario bajo',
          details: `${info.item_name} tiene stock bajo en los siguientes ingredientes: ${info.low_stock_ingredients.join(', ')}. Se recomienda reabastecer pronto.`,
        };
      } else {
        return {
          title: 'Inventario bajo',
          details: `${info.item_name} tiene stock bajo. Se recomienda reabastecer pronto.`,
        };
      }
    }
  };

  const { title, details } = buildMessage();
  const variant = isShortage ? 'danger' : 'warning';
  const Icon = isShortage ? FaBan : FaExclamationTriangle;

  return (
    <Alert variant={variant} className={`stock-alert stock-alert--${variant}`}>
      <div className="stock-alert__header">
        <Icon className="stock-alert__icon" />
        <strong>{title}</strong>
      </div>
      <div className="stock-alert__body">
        <FaInfoCircle className="stock-alert__info-icon" />
        <span>{details}</span>
      </div>
    </Alert>
  );
};

export default StockAlert;
